import type { CashbookAccess } from '@finvox/shared/cashbook-access';
import {
  allocateBulkPaymentFifo,
  assertCanVoidDebtIncurred,
  assertDrawerFundingAllowsPayment,
  assertLineDebtPaymentAllowed,
  cashbookClassifyFundingSchema,
  cashbookDrawerCountSchema,
  cashbookIncurDebtSchema,
  cashbookOpeningSchema,
  cashbookPayDebtBatchSchema,
  cashbookPayDebtBulkSchema,
  cashbookPayDebtSchema,
  cashbookRecordSchema,
  cashbookResetSchema,
  cashbookUpdateOpeningSchema,
  cashbookVoidDebtSchema,
  cashbookVoidSchema,
  CUSTOM_CASHBOOK_OUT_ACCOUNT_CODE,
  CUSTOM_CASHBOOK_IN_ACCOUNT_CODE,
  findCashbookCategory,
  mergeCashbookCategory,
  mergeDebtCategory,
  normalizeCashbookCategoryKey,
  CASHBOOK_DEBT_PAYMENT_CATEGORY,
  CASHBOOK_LOAN_RECEIPT_CATEGORY,
  computeOpenDebtRowsForOwner,
  creditorForPayDebtOwnerKey,
  debtCategoryByKey,
  debtLineHasCoveringPayments,
  hasStaleCashbookOpeningSettings,
  isCashbookOpeningConfigured,
  isDebtPaymentCategory,
  isLoanReceiptCategory,
  normalizeOpeningDebtLinesForWrite,
  openingDebtAmountOrZero,
  openingDebtLineEntryId,
  recordedLeftInDrawerForDay,
  salesDayKeyForDate,
  amountForNewDrawerSalesLine,
  sumOpeningDebtLines,
  localTodayIso,
  type CashbookCategory,
  type CashbookDebtCategory,
  type CashbookEntryRecord
} from '@finvox/shared/cashbook';
import { roundMoney, sumMoney, toMoney } from '@finvox/shared/money';
import {
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  type Transaction
} from 'firebase/firestore';
import { getFirestoreDb } from '@/lib/firebase';
import {
  assertEntryDateInPeriod,
  assertNotFutureDate,
  assertOnOrAfterOpening,
  assertPeriodOpen,
  bumpSyncVersion,
  cashbookDebtEntriesCol,
  cashbookEntriesCol,
  cashbookSettingsRef,
  hasActiveCashbookOrDebtEntries,
  invalidateCashbookContextCache,
  loadCashbookSettings,
  loadCurrentPeriodId,
  loadDebtEntries,
  loadActiveCashbookEntryIds,
  loadActiveDebtEntryIds,
  mapCashbookEntry,
  mapDebtEntry
} from './context';
import { applyDrawerSalesRefreshInTx, assertDrawerCountAllowed } from './salesDay';
import { assertCashbookWritesAllowed } from './writeGuard';

export type PermissionsContext = {
  post: boolean;
  import: boolean;
  void: boolean;
};

export type CashbookPermissions = CashbookAccess | PermissionsContext;

function syncVersionKey(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (
    typeof v === 'object' &&
    v !== null &&
    'toMillis' in v &&
    typeof (v as { toMillis: () => number }).toMillis === 'function'
  ) {
    return String((v as { toMillis: () => number }).toMillis());
  }
  return String(v);
}

async function assertSyncUnchangedInTx(
  tx: Transaction,
  orgId: string,
  expected: string | null
): Promise<void> {
  const db = getFirestoreDb();
  const snap = await tx.get(cashbookSettingsRef(db, orgId));
  const current = syncVersionKey(snap.data()?.syncVersion);
  if (expected != null && current != null && expected !== current) {
    throw new Error('Cashbook data changed — refresh and try again');
  }
}

function assertCanPost(permissions: CashbookPermissions): void {
  if (!permissions.post) throw new Error('Insufficient permissions');
}

function assertCanVoid(permissions: CashbookPermissions): void {
  if (!permissions.void) throw new Error('Insufficient permissions');
}

async function loadActiveEntriesMapped(orgId: string): Promise<CashbookEntryRecord[]> {
  const db = getFirestoreDb();
  const snap = await getDocs(
    query(cashbookEntriesCol(db, orgId), where('status', '==', 'active'))
  );
  return snap.docs.map((d) => mapCashbookEntry(d.id, d.data()));
}

function debtCategoriesFromOpeningLines(
  lines: { categoryKey: string }[],
  existing: CashbookDebtCategory[] = []
): CashbookDebtCategory[] {
  let next = existing;
  for (const line of lines) {
    next = mergeDebtCategory(next, line.categoryKey);
  }
  return next;
}

export async function setCashbookOpening(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true }> {
  assertCanPost(permissions);
  const data = cashbookOpeningSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);

  const settings = await loadCashbookSettings(db, orgId);
  if (isCashbookOpeningConfigured(settings)) {
    throw new Error('Opening treasury already set');
  }

  if (await hasActiveCashbookOrDebtEntries(db, orgId)) {
    throw new Error('Cannot set opening after cashbook entries exist');
  }

  const amt = data.amount;
  if (!amt || toMoney(amt).lte(0)) throw new Error('Opening amount must be greater than zero');
  const debtOverrides = settings.debtCategories ?? [];
  const debtLines =
    data.debtLines !== undefined
      ? normalizeOpeningDebtLinesForWrite(data.debtLines, debtOverrides)
      : [];
  const debtAmt =
    data.debtLines !== undefined
      ? sumOpeningDebtLines(debtLines)
      : (data.debtAmount ?? '0.00');
  if (toMoney(debtAmt).lt(0)) throw new Error('Opening debt amount cannot be negative');
  const nextDebtCategories = debtCategoriesFromOpeningLines(debtLines, debtOverrides);

  await runTransaction(db, async (tx) => {
    tx.set(
      cashbookSettingsRef(db, orgId),
      {
        openingAmount: roundMoney(amt),
        openingDebtAmount: roundMoney(debtAmt),
        openingDebtLines: debtLines,
        openingDate: data.date,
        openingSetAt: serverTimestamp(),
        openingSetBy: uid,
        debtCategories: nextDebtCategories,
        syncVersion: serverTimestamp()
      },
      { merge: true }
    );
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true };
}

export async function updateCashbookOpening(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true }> {
  assertCanPost(permissions);
  const data = cashbookUpdateOpeningSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) {
    throw new Error('Opening treasury is not set');
  }
  if (await hasActiveCashbookOrDebtEntries(db, orgId)) {
    throw new Error('Cannot edit opening after cashbook or debt entries exist');
  }

  const amt = data.amount;
  if (!amt || toMoney(amt).lte(0)) throw new Error('Opening amount must be greater than zero');
  const debtOverrides = settings.debtCategories ?? [];
  const debtLines =
    data.debtLines !== undefined
      ? normalizeOpeningDebtLinesForWrite(data.debtLines, debtOverrides)
      : (settings.openingDebtLines ?? []);
  const debtAmt =
    data.debtLines !== undefined
      ? sumOpeningDebtLines(debtLines)
      : (data.debtAmount ?? openingDebtAmountOrZero(settings));
  if (toMoney(debtAmt).lt(0)) throw new Error('Opening debt amount cannot be negative');
  const nextDebtCategories = debtCategoriesFromOpeningLines(debtLines, debtOverrides);

  if (data.debtLines !== undefined) {
    const debtEntries = await loadDebtEntries(db, orgId, localTodayIso(), settings.openingDate);
    const nextIds = new Set(debtLines.map((l) => openingDebtLineEntryId(l.id)));
    for (const prev of settings.openingDebtLines ?? []) {
      const entryId = openingDebtLineEntryId(prev.id);
      if (nextIds.has(entryId)) continue;
      if (debtLineHasCoveringPayments(debtEntries, entryId)) {
        throw new Error('Cannot remove opening debt line — payments already recorded against it');
      }
    }
  }

  await runTransaction(db, async (tx) => {
    tx.set(
      cashbookSettingsRef(db, orgId),
      {
        openingAmount: roundMoney(amt),
        openingDebtAmount: roundMoney(debtAmt),
        openingDebtLines: debtLines,
        openingDate: data.date,
        openingSetAt: serverTimestamp(),
        openingSetBy: uid,
        debtCategories: nextDebtCategories,
        syncVersion: serverTimestamp()
      },
      { merge: true }
    );
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true };
}

export async function recordCashbookEntry(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; entryId: string }> {
  assertCanPost(permissions);
  const data = cashbookRecordSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);
  await assertEntryDateInPeriod(db, orgId, data.periodId, data.date);
  assertNotFutureDate(data.date);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) throw new Error('Set opening treasury first');
  assertOnOrAfterOpening(settings, data.date);

  if (isDebtPaymentCategory(data.categoryKey)) {
    throw new Error('Use Pay debt to record debt payments');
  }
  if (isLoanReceiptCategory(data.categoryKey)) {
    throw new Error('Use Add debt with Borrowed money to record loan cash');
  }

  const overrides = settings.categories ?? [];
  const rawCategoryKey = normalizeCashbookCategoryKey(data.categoryKey);
  if (!rawCategoryKey) throw new Error('Invalid category');

  let cat = findCashbookCategory(rawCategoryKey, overrides);
  let nextCategories: CashbookCategory[] | undefined;
  if (!cat) {
    if (data.type === 'out') {
      cat = {
        key: rawCategoryKey,
        type: 'out',
        accountCode: CUSTOM_CASHBOOK_OUT_ACCOUNT_CODE
      };
    } else if (data.type === 'in') {
      if (rawCategoryKey.toLowerCase() === 'sales') {
        throw new Error('Use Left in drawer or PayVox for sales');
      }
      if (rawCategoryKey.toLowerCase() === CASHBOOK_LOAN_RECEIPT_CATEGORY) {
        throw new Error('Use Add debt with Borrowed money to record loan cash');
      }
      cat = {
        key: rawCategoryKey,
        type: 'in',
        accountCode: CUSTOM_CASHBOOK_IN_ACCOUNT_CODE
      };
    } else {
      throw new Error('Invalid category');
    }
    nextCategories = mergeCashbookCategory(overrides, cat);
  } else if (cat.type !== data.type) {
    throw new Error('Invalid category');
  }

  if (isDebtPaymentCategory(cat.key)) {
    throw new Error('Use Pay debt to record debt payments');
  }
  if (isLoanReceiptCategory(cat.key)) {
    throw new Error('Use Add debt with Borrowed money to record loan cash');
  }

  const categoryKey = cat.key;
  const amt = data.amount;
  if (toMoney(amt).lte(0)) throw new Error('Amount must be positive');

  const entryRef = doc(cashbookEntriesCol(db, orgId));
  const memo = data.memo?.trim() || undefined;
  const fundingSource = data.type === 'out' ? data.fundingSource : undefined;

  const existing = await loadActiveEntriesMapped(orgId);
  const nextEntries: CashbookEntryRecord[] = [
    ...existing,
    {
      id: entryRef.id,
      type: data.type,
      amount: roundMoney(amt),
      date: data.date,
      categoryKey,
      status: 'active',
      ...(fundingSource ? { fundingSource } : {}),
      ...(memo ? { memo } : {})
    }
  ];
  const expectedSync = syncVersionKey(settings.syncVersion);

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    tx.set(entryRef, {
      type: data.type,
      amount: roundMoney(amt),
      date: data.date,
      categoryKey,
      ...(memo ? { memo } : {}),
      ...(fundingSource ? { fundingSource } : {}),
      periodId: data.periodId,
      status: 'active',
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    if (data.type === 'out') {
      applyDrawerSalesRefreshInTx(tx, db, orgId, data.date, nextEntries);
    }
    if (nextCategories) {
      tx.set(
        cashbookSettingsRef(db, orgId),
        { categories: nextCategories, syncVersion: serverTimestamp() },
        { merge: true }
      );
    } else {
      bumpSyncVersion(tx, db, orgId);
    }
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true, entryId: entryRef.id };
}

async function voidDebtEntryById(
  orgId: string,
  uid: string,
  entryId: string,
  options?: { skipCashCascade?: boolean }
): Promise<void> {
  assertCashbookWritesAllowed();
  const db = getFirestoreDb();
  const ref = doc(db, 'organizations', orgId, 'cashbookDebtEntries', entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Debt entry not found');
  const data = snap.data()!;
  if (data.status === 'voided') throw new Error('Entry already voided');

  const periodId = String(data.periodId ?? '');
  await assertPeriodOpen(db, orgId, periodId);

  const settings = await loadCashbookSettings(db, orgId);
  const asOf = localTodayIso();
  const debtEntries = await loadDebtEntries(db, orgId, asOf, settings.openingDate);
  const entry = mapDebtEntry(entryId, data);
  assertCanVoidDebtIncurred(settings, debtEntries, entry, asOf);

  const linkedCashId = data.linkedCashbookEntryId
    ? String(data.linkedCashbookEntryId)
    : undefined;

  let cashDate: string | undefined;
  let nextEntries: CashbookEntryRecord[] | undefined;
  if (!options?.skipCashCascade && linkedCashId) {
    const cashSnap = await getDoc(doc(db, 'organizations', orgId, 'cashbookEntries', linkedCashId));
    if (cashSnap.exists()) {
      cashDate = String(cashSnap.data()?.date ?? '');
      const existing = await loadActiveEntriesMapped(orgId);
      nextEntries = existing.filter((e) => e.id !== linkedCashId);
    }
  }

  const expectedSync = syncVersionKey(settings.syncVersion);

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    tx.update(ref, {
      status: 'voided',
      voidedAt: serverTimestamp(),
      voidedBy: uid
    });
    if (!options?.skipCashCascade && linkedCashId) {
      const cashRef = doc(db, 'organizations', orgId, 'cashbookEntries', linkedCashId);
      tx.update(cashRef, {
        status: 'voided',
        voidedAt: serverTimestamp(),
        voidedBy: uid
      });
      if (cashDate && nextEntries) {
        applyDrawerSalesRefreshInTx(tx, db, orgId, cashDate, nextEntries);
      }
    }
    bumpSyncVersion(tx, db, orgId);
  });
}

async function voidCashbookEntryById(
  orgId: string,
  uid: string,
  entryId: string,
  options?: { skipDebtCascade?: boolean }
): Promise<void> {
  assertCashbookWritesAllowed();
  const db = getFirestoreDb();
  const ref = doc(db, 'organizations', orgId, 'cashbookEntries', entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Entry not found');
  const data = snap.data()!;
  if (data.status === 'voided') throw new Error('Entry already voided');

  const periodId = String(data.periodId ?? '');
  await assertPeriodOpen(db, orgId, periodId);

  const linkedDebtId = data.linkedDebtEntryId ? String(data.linkedDebtEntryId) : undefined;
  const entryDate = String(data.date ?? '');
  const entryType = data.type === 'in' ? 'in' : 'out';
  const existing = await loadActiveEntriesMapped(orgId);
  const nextEntries = existing.filter((e) => e.id !== entryId);
  const settings = await loadCashbookSettings(db, orgId);
  const expectedSync = syncVersionKey(settings.syncVersion);

  if (!options?.skipDebtCascade && linkedDebtId) {
    const debtSnap = await getDoc(doc(db, 'organizations', orgId, 'cashbookDebtEntries', linkedDebtId));
    if (debtSnap.exists() && debtSnap.data()?.status !== 'voided') {
      const debtData = debtSnap.data()!;
      if (debtData.type === 'incurred') {
        const asOf = localTodayIso();
        const debtEntries = await loadDebtEntries(db, orgId, asOf, settings.openingDate);
        const entry = mapDebtEntry(linkedDebtId, debtData);
        assertCanVoidDebtIncurred(settings, debtEntries, entry, asOf);
      }
    }
  }

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    tx.update(ref, {
      status: 'voided',
      voidedAt: serverTimestamp(),
      voidedBy: uid
    });
    if (!options?.skipDebtCascade && linkedDebtId) {
      const debtRef = doc(db, 'organizations', orgId, 'cashbookDebtEntries', linkedDebtId);
      tx.update(debtRef, {
        status: 'voided',
        voidedAt: serverTimestamp(),
        voidedBy: uid
      });
    }
    if (entryType === 'out' && entryDate) {
      applyDrawerSalesRefreshInTx(tx, db, orgId, entryDate, nextEntries);
    }
    bumpSyncVersion(tx, db, orgId);
  });
}

export async function voidCashbookEntry(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true }> {
  assertCanVoid(permissions);
  const { entryId } = cashbookVoidSchema.parse(input);
  await voidCashbookEntryById(orgId, uid, entryId);
  invalidateCashbookContextCache(orgId);
  return { ok: true };
}

export async function incurCashbookDebt(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; entryId: string; cashEntryId?: string }> {
  assertCashbookWritesAllowed();
  assertCanPost(permissions);
  const data = cashbookIncurDebtSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);
  await assertEntryDateInPeriod(db, orgId, data.periodId, data.date);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) throw new Error('Set opening treasury first');
  assertOnOrAfterOpening(settings, data.date);
  assertNotFutureDate(data.date);

  if (!normalizeCashbookCategoryKey(data.categoryKey)) {
    throw new Error('Invalid debt category');
  }
  const debtOverrides = settings.debtCategories ?? [];
  let debtCat = debtCategoryByKey(data.categoryKey, debtOverrides);
  let nextDebtCategories: ReturnType<typeof mergeDebtCategory> | undefined;
  if (!debtCat) {
    const key = normalizeCashbookCategoryKey(data.categoryKey);
    debtCat = { key, labelKey: key };
    nextDebtCategories = mergeDebtCategory(debtOverrides, debtCat);
  }
  if (toMoney(data.amount).lte(0)) throw new Error('Amount must be positive');

  const entryRef = doc(cashbookDebtEntriesCol(db, orgId));
  const creditor = data.creditor.trim();
  const memo = data.memo?.trim();
  const categoryKey = debtCat.key;
  const linkLoanCash = categoryKey === 'loan';
  const cashRef = linkLoanCash ? doc(cashbookEntriesCol(db, orgId)) : undefined;
  const expectedSync = syncVersionKey(settings.syncVersion);

  await runTransaction(db, async (tx) => {
    if (linkLoanCash) {
      await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    }
    tx.set(entryRef, {
      type: 'incurred',
      amount: roundMoney(data.amount),
      date: data.date,
      categoryKey,
      creditor,
      ...(memo ? { memo } : {}),
      periodId: data.periodId,
      status: 'active',
      ...(cashRef ? { linkedCashbookEntryId: cashRef.id } : {}),
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    if (cashRef) {
      tx.set(cashRef, {
        type: 'in',
        amount: roundMoney(data.amount),
        date: data.date,
        categoryKey: CASHBOOK_LOAN_RECEIPT_CATEGORY,
        ...(memo ? { memo } : {}),
        periodId: data.periodId,
        status: 'active',
        linkedDebtEntryId: entryRef.id,
        createdBy: uid,
        createdAt: serverTimestamp()
      });
    }
    if (nextDebtCategories) {
      tx.set(
        cashbookSettingsRef(db, orgId),
        { debtCategories: nextDebtCategories, syncVersion: serverTimestamp() },
        { merge: true }
      );
    } else {
      bumpSyncVersion(tx, db, orgId);
    }
  });

  invalidateCashbookContextCache(orgId);
  return {
    ok: true,
    entryId: entryRef.id,
    ...(cashRef ? { cashEntryId: cashRef.id } : {})
  };
}

export async function payCashbookDebt(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; debtEntryId: string; cashEntryId: string }> {
  assertCashbookWritesAllowed();
  assertCanPost(permissions);
  const data = cashbookPayDebtSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);
  await assertEntryDateInPeriod(db, orgId, data.periodId, data.date);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) throw new Error('Set opening treasury first');
  assertOnOrAfterOpening(settings, data.date);
  assertNotFutureDate(data.date);

  const asOf = localTodayIso();
  const debtEntries = await loadDebtEntries(db, orgId, asOf, settings.openingDate);
  const ownerKey = data.creditor.trim();
  const openRows = computeOpenDebtRowsForOwner(settings, debtEntries, asOf, ownerKey);

  let allocations: { debtEntryId: string; amount: string }[];
  if (data.appliedToDebtEntryId) {
    const row = openRows.find((r) => r.id === data.appliedToDebtEntryId);
    if (!row) throw new Error('Selected debt line is not open for this owner');
    assertLineDebtPaymentAllowed(row.remainingAmount, data.amount);
    allocations = [{ debtEntryId: data.appliedToDebtEntryId, amount: roundMoney(data.amount) }];
  } else {
    allocations = allocateBulkPaymentFifo(openRows, data.amount);
  }

  const cashEntries = await loadActiveEntriesMapped(orgId);
  const leftInDrawer = recordedLeftInDrawerForDay(cashEntries, data.date);
  assertDrawerFundingAllowsPayment(data.fundingSource, data.amount, leftInDrawer);

  const expectedSync = syncVersionKey(settings.syncVersion);
  const storedCreditor = creditorForPayDebtOwnerKey(ownerKey);
  const debtRef = doc(cashbookDebtEntriesCol(db, orgId));
  const cashRef = doc(cashbookEntriesCol(db, orgId));
  const memo = data.memo?.trim() || undefined;
  const nextEntries: CashbookEntryRecord[] = [
    ...cashEntries,
    {
      id: cashRef.id,
      type: 'out',
      amount: roundMoney(data.amount),
      date: data.date,
      categoryKey: CASHBOOK_DEBT_PAYMENT_CATEGORY,
      status: 'active',
      fundingSource: data.fundingSource
    }
  ];

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    tx.set(cashRef, {
      type: 'out',
      amount: roundMoney(data.amount),
      date: data.date,
      categoryKey: CASHBOOK_DEBT_PAYMENT_CATEGORY,
      fundingSource: data.fundingSource,
      ...(memo ? { memo } : {}),
      periodId: data.periodId,
      status: 'active',
      linkedDebtEntryId: debtRef.id,
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    tx.set(debtRef, {
      type: 'payment',
      amount: roundMoney(data.amount),
      date: data.date,
      categoryKey: 'supplier',
      ...(memo ? { memo } : {}),
      ...(storedCreditor ? { creditor: storedCreditor } : {}),
      allocations,
      ...(allocations.length === 1
        ? { appliedToDebtEntryId: allocations[0]!.debtEntryId }
        : {}),
      periodId: data.periodId,
      status: 'active',
      linkedCashbookEntryId: cashRef.id,
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    applyDrawerSalesRefreshInTx(tx, db, orgId, data.date, nextEntries);
    bumpSyncVersion(tx, db, orgId);
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true, debtEntryId: debtRef.id, cashEntryId: cashRef.id };
}

/**
 * Bulk pay: one total amount, FIFO oldest-first, one cash out + one debt payment with frozen allocations.
 * Also accepts legacy `{ lines: [...] }` batch shape and consolidates into one record.
 */
export async function payCashbookDebtBatch(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; count: number; debtEntryId: string; cashEntryId: string }> {
  assertCashbookWritesAllowed();
  assertCanPost(permissions);

  const bulkParsed = cashbookPayDebtBulkSchema.safeParse(input);
  const legacyParsed = cashbookPayDebtBatchSchema.safeParse(input);

  let amount: string;
  let date: string;
  let periodId: string;
  let creditor: string;
  let memo: string | undefined;
  let fundingSource: 'drawer' | 'treasury';
  let forcedAllocations: { debtEntryId: string; amount: string }[] | null = null;

  if (legacyParsed.success && Array.isArray((input as { lines?: unknown }).lines)) {
    const data = legacyParsed.data;
    const payLines = data.lines.filter((l) => toMoney(l.amount).gt(0));
    if (payLines.length === 0) throw new Error('Amount must be positive');
    amount = sumMoney(payLines.map((l) => l.amount));
    date = data.date;
    periodId = data.periodId;
    creditor = data.creditor;
    memo = data.memo?.trim() || undefined;
    fundingSource = data.fundingSource;
    forcedAllocations = payLines.map((l) => ({
      debtEntryId: l.appliedToDebtEntryId,
      amount: roundMoney(l.amount)
    }));
  } else if (bulkParsed.success) {
    amount = bulkParsed.data.amount;
    date = bulkParsed.data.date;
    periodId = bulkParsed.data.periodId;
    creditor = bulkParsed.data.creditor;
    memo = bulkParsed.data.memo?.trim() || undefined;
    fundingSource = bulkParsed.data.fundingSource;
  } else {
    throw bulkParsed.success === false ? bulkParsed.error : legacyParsed.error;
  }

  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, periodId);
  await assertEntryDateInPeriod(db, orgId, periodId, date);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) throw new Error('Set opening treasury first');
  assertOnOrAfterOpening(settings, date);
  assertNotFutureDate(date);

  const asOf = localTodayIso();
  const debtEntries = await loadDebtEntries(db, orgId, asOf, settings.openingDate);
  const ownerKey = creditor.trim();
  const openRows = computeOpenDebtRowsForOwner(settings, debtEntries, asOf, ownerKey);
  const openById = new Map(openRows.map((r) => [r.id, r]));

  let allocations: { debtEntryId: string; amount: string }[];
  if (forcedAllocations) {
    const seen = new Set<string>();
    for (const line of forcedAllocations) {
      if (seen.has(line.debtEntryId)) throw new Error('Duplicate debt line in payment batch');
      seen.add(line.debtEntryId);
      const row = openById.get(line.debtEntryId);
      if (!row) throw new Error('Selected debt line is not open for this owner');
      assertLineDebtPaymentAllowed(row.remainingAmount, line.amount);
    }
    allocations = forcedAllocations;
  } else {
    allocations = allocateBulkPaymentFifo(openRows, amount);
  }

  const cashEntries = await loadActiveEntriesMapped(orgId);
  const leftInDrawer = recordedLeftInDrawerForDay(cashEntries, date);
  assertDrawerFundingAllowsPayment(fundingSource, amount, leftInDrawer);

  const expectedSync = syncVersionKey(settings.syncVersion);
  const storedCreditor = creditorForPayDebtOwnerKey(ownerKey);
  const debtRef = doc(cashbookDebtEntriesCol(db, orgId));
  const cashRef = doc(cashbookEntriesCol(db, orgId));
  const nextEntries: CashbookEntryRecord[] = [
    ...cashEntries,
    {
      id: cashRef.id,
      type: 'out',
      amount: roundMoney(amount),
      date,
      categoryKey: CASHBOOK_DEBT_PAYMENT_CATEGORY,
      status: 'active',
      fundingSource
    }
  ];

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    tx.set(cashRef, {
      type: 'out',
      amount: roundMoney(amount),
      date,
      categoryKey: CASHBOOK_DEBT_PAYMENT_CATEGORY,
      fundingSource,
      ...(memo ? { memo } : {}),
      periodId,
      status: 'active',
      linkedDebtEntryId: debtRef.id,
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    tx.set(debtRef, {
      type: 'payment',
      amount: roundMoney(amount),
      date,
      categoryKey: 'supplier',
      ...(memo ? { memo } : {}),
      ...(storedCreditor ? { creditor: storedCreditor } : {}),
      allocations,
      ...(allocations.length === 1
        ? { appliedToDebtEntryId: allocations[0]!.debtEntryId }
        : {}),
      periodId,
      status: 'active',
      linkedCashbookEntryId: cashRef.id,
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    applyDrawerSalesRefreshInTx(tx, db, orgId, date, nextEntries);
    bumpSyncVersion(tx, db, orgId);
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true, count: 1, debtEntryId: debtRef.id, cashEntryId: cashRef.id };
}

export async function voidCashbookDebtEntry(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true }> {
  assertCanVoid(permissions);
  const { entryId } = cashbookVoidDebtSchema.parse(input);
  await voidDebtEntryById(orgId, uid, entryId);
  invalidateCashbookContextCache(orgId);
  return { ok: true };
}

async function clearCashbookOpening(
  orgId: string,
  _uid: string,
  periodId: string,
  permissions: CashbookPermissions
): Promise<void> {
  assertCanPost(permissions);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, periodId);

  if (await hasActiveCashbookOrDebtEntries(db, orgId)) {
    throw new Error('Cannot edit opening after cashbook or debt entries exist');
  }

  const settings = await loadCashbookSettings(db, orgId);
  const hadSettings =
    isCashbookOpeningConfigured(settings) || hasStaleCashbookOpeningSettings(settings);
  if (!hadSettings) return;

  await runTransaction(db, async (tx) => {
    tx.set(
      cashbookSettingsRef(db, orgId),
      {
        openingAmount: deleteField(),
        openingDebtAmount: deleteField(),
        openingDebtLines: deleteField(),
        openingDate: deleteField(),
        openingSetAt: deleteField(),
        openingSetBy: deleteField(),
        syncVersion: serverTimestamp()
      },
      { merge: true }
    );
  });
}

async function tryClearCashbookOpening(
  orgId: string,
  uid: string,
  permissions: CashbookPermissions
): Promise<boolean> {
  const db = getFirestoreDb();
  const periodId = await loadCurrentPeriodId(db, orgId);
  if (!periodId) return false;
  try {
    await clearCashbookOpening(orgId, uid, periodId, permissions);
    return true;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (
      msg.includes('Insufficient permissions') ||
      msg.includes('Fiscal period is closed') ||
      msg.includes('Cannot edit opening after cashbook entries exist') ||
      msg.includes('Cannot edit opening after cashbook or debt entries exist')
    ) {
      return false;
    }
    throw e;
  }
}

export async function resetCashbook(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; voidedCount: number; skippedCount?: number; openingCleared?: boolean }> {
  const data = cashbookResetSchema.parse(input);

  if (data.scope === 'opening') {
    await clearCashbookOpening(orgId, uid, data.periodId, permissions);
    invalidateCashbookContextCache(orgId);
    return { ok: true, voidedCount: 0, openingCleared: true };
  }

  assertCanVoid(permissions);
  const db = getFirestoreDb();

  let entryIds: string[];
  let debtEntryIds: string[];
  if (data.scope === 'all') {
    entryIds = await loadActiveCashbookEntryIds(db, orgId);
    debtEntryIds = await loadActiveDebtEntryIds(db, orgId);
  } else if (data.scope === 'date') {
    entryIds = await loadActiveCashbookEntryIds(db, orgId, data.date, data.date);
    debtEntryIds = await loadActiveDebtEntryIds(db, orgId, data.date, data.date);
  } else {
    entryIds = await loadActiveCashbookEntryIds(db, orgId, data.startDate, data.endDate);
    debtEntryIds = await loadActiveDebtEntryIds(db, orgId, data.startDate, data.endDate);
  }

  let voidedCount = 0;
  let skippedCount = 0;
  for (const entryId of entryIds) {
    try {
      await voidCashbookEntryById(orgId, uid, entryId);
      voidedCount++;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === 'Entry already voided') continue;
      throw e;
    }
  }
  for (const entryId of debtEntryIds) {
    try {
      await voidDebtEntryById(orgId, uid, entryId, { skipCashCascade: true });
      voidedCount++;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === 'Entry already voided') continue;
      if (msg.includes('Cannot void — payments already recorded')) {
        skippedCount++;
        continue;
      }
      throw e;
    }
  }

  let openingCleared = false;
  if (data.scope === 'all' && !(await hasActiveCashbookOrDebtEntries(db, orgId))) {
    openingCleared = await tryClearCashbookOpening(orgId, uid, permissions);
  }

  if (voidedCount > 0) {
    await setDoc(cashbookSettingsRef(db, orgId), { syncVersion: serverTimestamp() }, { merge: true });
  }

  invalidateCashbookContextCache(orgId);
  return {
    ok: true,
    voidedCount,
    skippedCount: skippedCount > 0 ? skippedCount : undefined,
    openingCleared: openingCleared || undefined
  };
}

export async function classifyCashbookFunding(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true }> {
  assertCanPost(permissions);
  const data = cashbookClassifyFundingSchema.parse(input);
  const db = getFirestoreDb();
  const ref = doc(cashbookEntriesCol(db, orgId), data.entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Entry not found');
  const raw = snap.data()!;
  if (raw.status === 'voided') throw new Error('Entry already voided');
  if (raw.type !== 'out') throw new Error('Only expenses can be classified');
  const current = resolveFundingFromRaw(raw.fundingSource);
  if (current !== 'unclassified') {
    throw new Error('Expense already has a type');
  }

  const periodId = String(raw.periodId ?? '');
  await assertPeriodOpen(db, orgId, periodId);
  const entryDate = String(raw.date ?? '');
  const existing = await loadActiveEntriesMapped(orgId);
  const nextEntries = existing.map((e) =>
    e.id === data.entryId ? { ...e, fundingSource: data.fundingSource } : e
  );
  const settings = await loadCashbookSettings(db, orgId);
  const expectedSync = syncVersionKey(settings.syncVersion);

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    tx.update(ref, {
      fundingSource: data.fundingSource,
      fundingClassifiedAt: serverTimestamp(),
      fundingClassifiedBy: uid
    });
    if (entryDate) {
      applyDrawerSalesRefreshInTx(tx, db, orgId, entryDate, nextEntries);
    }
    bumpSyncVersion(tx, db, orgId);
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true };
}

function resolveFundingFromRaw(raw: unknown): 'drawer' | 'treasury' | 'unclassified' {
  if (raw === 'drawer' || raw === 'treasury') return raw;
  return 'unclassified';
}

export async function saveCashbookDrawerCount(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; entryId: string }> {
  assertCanPost(permissions);
  const data = cashbookDrawerCountSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);
  await assertEntryDateInPeriod(db, orgId, data.periodId, data.date);
  assertNotFutureDate(data.date);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) throw new Error('Set opening treasury first');
  assertOnOrAfterOpening(settings, data.date);

  if (toMoney(data.drawerCount).lt(0)) throw new Error('Amount cannot be negative');

  const existing = await loadActiveEntriesMapped(orgId);
  assertDrawerCountAllowed(existing, data.date);

  const salesAmount = amountForNewDrawerSalesLine(existing, data.date, data.drawerCount);
  const dayKey = salesDayKeyForDate(data.date);
  const memo = data.memo?.trim() || undefined;
  const expectedSync = syncVersionKey(settings.syncVersion);

  let entryId: string;

  await runTransaction(db, async (tx) => {
    await assertSyncUnchangedInTx(tx, orgId, expectedSync);
    const entryRef = doc(cashbookEntriesCol(db, orgId));
    entryId = entryRef.id;
    tx.set(entryRef, {
      type: 'in',
      amount: roundMoney(salesAmount),
      date: data.date,
      categoryKey: 'sales',
      drawerCount: roundMoney(data.drawerCount),
      salesDayKey: dayKey,
      salesOrigin: 'drawer',
      ...(memo ? { memo } : {}),
      periodId: data.periodId,
      status: 'active',
      createdBy: uid,
      createdAt: serverTimestamp()
    });
    bumpSyncVersion(tx, db, orgId);
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true, entryId: entryId! };
}
